/**
 * V2 mapper for customer live event bookings.
 * V2 API returns a FLAT structure - event details are directly on the booking,
 * not nested in a liveEvent object. This mapper converts to the nested domain shape.
 *
 * Pure TypeScript - no React imports.
 */

import type {
  CustomerLiveEventBooking,
  CustomerLiveEventDetails,
  PaginatedCustomerLiveEventBookings,
  CustomerLiveEventStatus,
} from "../../domain/types";
import type {JoinLiveEventResult} from "../../../live-events/domain/types";
import type {
  CustomerLiveEventBookingV2,
  CustomerLiveEventListV2Data,
  CustomerJoinLiveEventV2Data,
} from "./mapper.types";

export const CustomerLiveEventMapperV2 = {
  /**
   * Maps v2 flat booking response to domain booking.
   * V2 has flat structure; we construct the nested liveEvent from flat fields.
   */
  toBooking(response: CustomerLiveEventBookingV2): CustomerLiveEventBooking {
    // Build nested liveEvent from flat v2 fields
    const liveEvent: CustomerLiveEventDetails = {
      id: response.eventId,
      name: response.eventName,
      coverImage: response.imageUrl ?? "",
      videoPlatform: response.videoPlatform ?? "",
      roomId: "", // v2 doesn't return roomId in list response
      attendees: response.totalAttendees,
      locationAddress: response.locationAddress ?? null,
      locationLat: response.locationLat ?? null,
      locationLng: response.locationLng ?? null,
      // v2 doesn't return these
      description: undefined,
      callPrice: undefined,
      callPriceCurrency: undefined,
      isCustomRoom: undefined,
    };

    return {
      id: response.id,
      eventId: response.eventId,
      userId: 0, // v2 doesn't return userId
      reference: response.reference,
      firstName: response.firstName,
      lastName: response.lastName,
      emailAddress: response.emailAddress,
      eventDate: response.eventDate ?? "",
      customerEventTime: response.customerEventTime ?? "",
      customerEndTime: response.customerEndTime ?? "",
      coachEventTime: "", // v2 doesn't return coach times
      coachEndTime: "",
      amount: response.amount ?? 0,
      currency: response.currency ?? "",
      isPaid: response.amount !== null && response.amount > 0,
      paymentProvider: "", // v2 doesn't return paymentProvider
      statusId: 0, // v2 doesn't return statusId
      statusName: "Confirmed" as CustomerLiveEventStatus, // Default for upcoming
      isSeries: false, // v2 doesn't return isSeries
      currentSession: 0, // v2 doesn't return currentSession
      liveEvent,
      createdAt: response.createdAt,
      updatedAt: undefined,
    };
  },

  /**
   * Maps v2 paginated list data to domain.
   */
  toBookingList(data: CustomerLiveEventListV2Data): PaginatedCustomerLiveEventBookings {
    return {
      data: data.data.map(CustomerLiveEventMapperV2.toBooking),
      total: data.total,
      hasNextPage: data.hasNextPage,
      hasPrevPage: data.hasPrevPage,
    };
  },

  /**
   * Maps v2 join data to unified domain result.
   * V2 shape matches domain - passthrough with nullish coalescing.
   */
  joinToDomain(data: CustomerJoinLiveEventV2Data): JoinLiveEventResult {
    return {
      token: data.token,
      roomId: data.roomId,
      videoPlatform: data.videoPlatform,
      customRoomLink: data.customRoomLink ?? null,
      dailyCoUrl: data.dailyCoUrl ?? null,
    };
  },
};
